'use client';

import { useState, useCallback, useRef } from 'react';
import { useSocket } from './useSocket';
import { useChatStore } from '@/store/chatStore';
import { useUIStore } from '@/store/uiStore';

const TYPING_DEBOUNCE_MS = 1500;

export function useChat() {
  const { sendMessage, sendTypingStart, sendTypingStop, findStranger, skipStranger, leaveChat, socketRef,
    sendFriendRequest, acceptFriendRequest, rejectFriendRequest } =
    useSocket();
  const { status, messages, strangerTyping, soundEnabled } = useChatStore();
  const { openReportModal } = useUIStore();

  const [inputValue, setInputValue] = useState('');
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);

      if (status !== 'connected') return;

      if (!isTypingRef.current) {
        isTypingRef.current = true;
        sendTypingStart();
      }

      // Debounce: stop typing after inactivity
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      typingTimerRef.current = setTimeout(() => {
        isTypingRef.current = false;
        sendTypingStop();
      }, TYPING_DEBOUNCE_MS);
    },
    [status, sendTypingStart, sendTypingStop]
  );

  const handleSendMessage = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || status !== 'connected') return;

    sendMessage(trimmed);
    setInputValue('');

    // Stop typing indicator
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    isTypingRef.current = false;
    sendTypingStop();
  }, [inputValue, status, sendMessage, sendTypingStop]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const handleReport = useCallback(() => {
    openReportModal();
  }, [openReportModal]);

  return {
    // State
    inputValue,
    status,
    messages,
    strangerTyping,
    soundEnabled,
    socketRef,

    // Actions
    handleInputChange,
    handleSendMessage,
    handleKeyDown,
    handleReport,
    findStranger,
    skipStranger,
    leaveChat,
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
  };
}
